// Generated by Copilot
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import {
    PackagingFormatInput,
    PourTypeInput,
    ProductCategoryInput,
    ServingContainerInput,
} from '@barboxx/admin-components';
import { CREATE_POUR_FORMAT_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { PourFormat } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreatePourFormatDialog = ({ onClose, onSuccess, open }: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<PourFormat.CreateRequest>({
        resolver: yupResolver(CREATE_POUR_FORMAT_SCHEMA),
        defaultValues: {},
    });
    const mutateCreatePourFormat = useMutation({
        mutationKey: ['pourFormat'],
        mutationFn: (data: PourFormat.CreateRequest) =>
            Api.Servings.PourFormat.postPourFormat(data),
        onSuccess: () => {
            formContext.reset();
        },
    });

    const onSubmit = async (values: PourFormat.CreateRequest) => {
        return mutateCreatePourFormat
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created!`, {
                    severity: 'success',
                });
                onClose();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating pour format!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancelCreate}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Pour Format
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <PourTypeInput />
                    <ServingContainerInput />
                    <PackagingFormatInput />
                    <ProductCategoryInput />
                    <TextFieldElement
                        fullWidth
                        name="volumeOz"
                        label="Volume (oz)"
                        type="number"
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                max: 1000,
                                step: 0.1,
                            },
                        }}
                    />
                    <TextFieldElement
                        fullWidth
                        name="volumeMl"
                        label="Volume (ml)"
                        type="number"
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                max: 10000,
                                step: 1,
                            },
                        }}
                    />
                    <TextFieldElement
                        fullWidth
                        name="displayOrder"
                        label="Display Order"
                        type="number"
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                max: 1000,
                                step: 1,
                            },
                        }}
                    />
                    <CheckboxElement label="Is Default" name="isDefault" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
